import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAsyncData } from './useAsyncData.js';

/**
 * Client-side paging over an already-loaded array. Use for naturally-bounded
 * lists (accounts, users, carriers) that also feed dropdowns, so the full set
 * stays in memory while the grid renders one page at a time.
 */
export function useClientPaged<T>(items: T[], pageSize = 20) {
  const [offset, setOffset] = useState(0);

  // If the list shrank past the current page (e.g. after a delete), step back.
  useEffect(() => {
    if (offset > 0 && offset >= items.length) {
      setOffset(Math.max(0, Math.floor((items.length - 1) / pageSize) * pageSize));
    }
  }, [items.length, offset, pageSize]);

  const pageItems = useMemo(
    () => items.slice(offset, offset + pageSize),
    [items, offset, pageSize],
  );
  return {
    pageItems,
    offset,
    count: pageItems.length,
    total: items.length,
    hasMore: offset + pageSize < items.length,
    next: () => setOffset((o) => (o + pageSize < items.length ? o + pageSize : o)),
    prev: () => setOffset((o) => Math.max(0, o - pageSize)),
  };
}

interface ServerPage<T> {
  data: T[];
  pagination?: { total?: number; hasMore: boolean };
}

/**
 * Server-side paging for high-growth grids (jobs, logs). `fetchPage` MUST be
 * stable (wrap in useCallback); it receives the current offset + limit and
 * returns one page. Changing the page re-runs the loader via useAsyncData.
 */
export function useServerPaged<T>(
  fetchPage: (offset: number, limit: number) => Promise<ServerPage<T>>,
  limit = 25,
) {
  const [offset, setOffset] = useState(0);
  const loader = useCallback(() => fetchPage(offset, limit), [fetchPage, offset, limit]);
  const { data, error, loading, reload } = useAsyncData(loader);
  const items = data?.data ?? [];
  return {
    items,
    error,
    loading,
    reload,
    offset,
    limit,
    count: items.length,
    total: data?.pagination?.total,
    hasMore: data?.pagination?.hasMore ?? false,
    next: () => setOffset((o) => o + limit),
    prev: () => setOffset((o) => Math.max(0, o - limit)),
  };
}
